import React, { useEffect, useState, useRef } from "react"
import Skeleton from "react-loading-skeleton"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { LineChart } from "@mui/x-charts"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"

const INITIAL_FILTERS = { category_id: "", date_from: null, date_to: null }

function AverageRepairTime() {
  const [loading, setLoading] = useState(true)
  const [chartData, setChartData] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [categories, setCategories] = useState([])
  const [avarage, setAvarage] = useState(null)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      setLoading(true)

      const f = `${filters.date_from ? `&date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `&date_to=${filters.date_to || ""}` : ""}`

      Api.get(
        `analytics/average-repair-time?category_id=${filters.category_id}${f}`,
      )
        .then((res) => {
          setChartData(res.data.data)
          setCategories(res.data.categories || [])
          setLoading(false)
          if (filters.category_id === "") {
            setAvarage(res.data.totals[0])
          } else {
            setAvarage(
              res.data.totals.find(
                (t) => t.category_id === filters.category_id,
              ) || 0,
            )
          }
        })
        .catch(() => setLoading(false))
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  const labels = chartData.map((d) => d.label)
  const counts = chartData.map((d) => d.avg_time_minutes)

  function getIfAnyCustomers() {
    return chartData.some((item) => item.avg_time_minutes > 0)
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.category_id) count++
    if (filters.date_from || filters.date_to) count++
    return count
  }

  return (
    <div className="average-repair-time-chart">
      <div className="head">
        <h3>{trans("analytics.averageRepairTime")}</h3>
        <div className="right">
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DateRange
                  startDate={filters.date_from || ""}
                  endDate={filters.date_to || ""}
                  onStartDateChange={(date) =>
                    handleFilterChange("date_from", date)
                  }
                  onEndDateChange={(date) =>
                    handleFilterChange("date_to", date)
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <select
                  value={filters.category_id || ""}
                  name="category_id"
                  onChange={(e) =>
                    handleFilterChange("category_id", e.target.value)
                  }
                >
                  <option value="">{trans("random.all")}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={300} count={1} />
      ) : !getIfAnyCustomers() ? (
        <div
          className="alert warning"
          style={{
            marginBottom: 0,
            width: "100%",
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <>
          <p>{trans("analytics.averageRepairTime")}</p>
          <b>
            {avarage
              ? `${avarage.avg_time_h}${trans("random.hourShort")} ${!avarage.avg_time_m ? "" : trans("random.and")} ${avarage.avg_time_m ? `${avarage.avg_time_m} ${trans("random.min")}` : ""}`
              : `0 ${trans("random.min")}`}
          </b>
          <LineChart
            xAxis={[
              {
                data: labels,
                scaleType: "point",
              },
            ]}
            series={[
              {
                data: counts,
                area: true,
                color: "#181F67",
                showMark: true,
              },
            ]}
            height={287}
            grid={{ horizontal: true }}
            sx={{
              "& .MuiAreaElement-root": {
                fill: "url(#customers-gradient)",
              },
              "& .MuiLineElement-root": {
                strokeWidth: 1,
              },
              "& .MuiMarkElement-root": {
                stroke: "#fff",
                strokeWidth: 1,
                fill: "#181F67",
                r: 5,
              },
              "& .MuiChartsGrid-line": {
                stroke: "#e0e0e0",
                strokeDasharray: "4 2",
              },
            }}
          >
            <defs>
              <linearGradient
                id="customers-gradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#181F67" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#181F67" stopOpacity={0.02} />
              </linearGradient>
            </defs>
          </LineChart>
        </>
      )}
    </div>
  )
}

export default AverageRepairTime
